import { useEffect, useId, useRef } from 'react';

import { cn } from '@/lib/utils';
import type { HintCopy } from '@/shared/stores';
import { Button } from '@/shared/ui/shadcn/button';

interface CoachmarkProps {
  readonly copy: HintCopy;
  /** Called by the primary CTA when defined. */
  readonly onPrimary?: () => void;
  /** Called by the Skip button and as the secondary action. */
  readonly onSkip: () => void;
}

/**
 * Centered coachmark dialog. Anchor-relative placement was tried first and
 * was awkward across overlays / scrollers; centering keeps the surface
 * predictable while the spotlight still draws attention to the anchor.
 */
export function Coachmark({ copy, onPrimary, onSkip }: CoachmarkProps) {
  const primaryRef = useRef<HTMLButtonElement | null>(null);
  // Per-instance id so multiple coachmarks (or other dialogs sharing the page) never collide.
  const bodyId = useId();

  useEffect(() => {
    primaryRef.current?.focus();
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={copy.title}
      aria-describedby={bodyId}
      className={cn(
        'pointer-events-auto fixed left-1/2 top-1/2 z-[60] w-[min(28rem,calc(100vw-2rem))]',
        '-translate-x-1/2 -translate-y-1/2 rounded-lg border bg-popover p-4 text-popover-foreground shadow-lg'
      )}
    >
      <h3 className="text-sm font-semibold">{copy.title}</h3>
      <p id={bodyId} aria-live="polite" className="mt-1 text-sm text-muted-foreground">
        {copy.body}
      </p>
      <div className="mt-4 flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onSkip}>
          {copy.secondaryLabel ?? 'Skip'}
        </Button>
        {onPrimary !== undefined && (
          <Button ref={primaryRef} type="button" size="sm" onClick={onPrimary}>
            {copy.primaryLabel ?? 'Got it'}
          </Button>
        )}
      </div>
    </div>
  );
}
